/**
 * Admin Blog manager — typed helpers over /api/v1/admin/blog.
 *
 * Posts are markdown-bodied rows in the API's blog_posts table; the marketing
 * site bakes published ones in at build time, so publish/unpublish only reach
 * legacyorb.com after a site rebuild (triggerBlogDeploy).
 */
import type { BlogPost, BlogPostStatus } from '@legacyorb/shared-schemas';
import { apiClient } from './api-client';

export type { BlogPostStatus };

/**
 * The admin serialized shape lives in shared-schemas (blogPostSchema) and is
 * contract-tested against real API responses in apps/api — one source of
 * truth instead of a hand-typed copy. `content` is absent on list responses.
 */
export type AdminBlogPost = BlogPost;

export interface BlogConfig {
  assetsConfigured: boolean;
  assetsPublicBaseUrl: string | null;
  deployConfigured: boolean;
}

export interface BlogDeployResult {
  triggered: boolean;
  environment: 'dev' | 'prod';
  message: string;
}

export const blogKeys = {
  posts: (status?: BlogPostStatus | 'all') => ['admin', 'blog', 'posts', status ?? 'all'] as const,
  post: (id: string) => ['admin', 'blog', 'post', id] as const,
  config: ['admin', 'blog', 'config'] as const,
};

/**
 * Uploaded images are stored as root-relative paths (/blog/assets/…) served
 * by the marketing CloudFront distribution — prefix them with the marketing
 * base URL (from GET /admin/blog/config) to preview them from the admin.
 */
export function resolveBlogImageUrl(
  image: string | null | undefined,
  assetsPublicBaseUrl: string | null | undefined
): string | undefined {
  if (!image) return undefined;
  if (!image.startsWith('/')) return image;
  const base = (assetsPublicBaseUrl ?? '').replace(/\/$/, '');
  return `${base}${image}`;
}

/**
 * Publish/unpublish — the one place the status-toggle endpoints are named,
 * shared by the list page and the editor.
 */
export function toggleBlogPostStatus(post: Pick<AdminBlogPost, 'id' | 'status'>) {
  const action = post.status === 'published' ? 'unpublish' : 'publish';
  return apiClient.post<AdminBlogPost>(`/api/v1/admin/blog/posts/${post.id}/${action}`);
}

/**
 * Byline dates render in UTC on every surface (the marketing site builds in
 * CI, this admin runs in the editor's browser) so both always show the same
 * calendar day for a given instant.
 */
export function formatBlogDate(iso: string, style: 'short' | 'long' = 'short'): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: style === 'short' ? 'short' : 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Multipart upload — bypasses apiClient.post (which forces JSON) per the
 * api-client's documented getBaseUrl()/getToken() escape hatch.
 */
export async function uploadBlogAsset(file: File): Promise<{ path: string; url: string }> {
  const token = await apiClient.getToken();
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${apiClient.getBaseUrl()}/api/v1/admin/blog/assets`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new Error(body?.error?.message || `Upload failed with status ${res.status}`);
  }
  return body.data;
}
